import { Editor } from "@monaco-editor/react";
import { useTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { SelectItem } from "primereact/selectitem";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { useUserState } from "store/user-store";
import { DATE_PATTERN_MINUTES, reactQuillModules } from "utils/constants";
import { DweenTimeUnitEnum } from "utils/enums";
import { replaceVariables, VariableReplacementContext } from "utils/variable-replacer";
import { FreeTextConfig, FreeTextDateVariable } from "./FreeTextConfig";

interface FreeTextEditorProps {
  config: FreeTextConfig;
  onConfigChange: (config: FreeTextConfig) => void;
}

const timeUnitOptions: SelectItem[] = Object.keys(DweenTimeUnitEnum)
  .filter(key => key !== DweenTimeUnitEnum.HALFHOUR)
  .map(key => ({
    label: key,
    value: key,
  }));

const DEFAULT_VARIABLE: FreeTextDateVariable = {
  name: "newVariable",
  truncateTo: DweenTimeUnitEnum.HOURS,
  offsetUnit: DweenTimeUnitEnum.HOURS,
  offset: 0,
  format: DATE_PATTERN_MINUTES,
};

export default function FreeTextEditor({ config, onConfigChange }: FreeTextEditorProps) {
  const [activeIndex, setActiveIndex] = useState(config.useHTMLCode ? 1 : 0);
  const [variables, setVariables] = useState<FreeTextDateVariable[]>(config.variables ?? []);
  const dashboard = useDashboardStore(state => state.dashboard);
  const tz = useTimezone();
  const locale = useUserState(state => state.getLanguage());

  const handleTabChange = (index: number) => {
    setActiveIndex(index);
    onConfigChange({ ...config, useHTMLCode: index === 1 });
  };

  const updateVariables = (newVariables: FreeTextDateVariable[]) => {
    setVariables(newVariables);
    onConfigChange({ ...config, variables: newVariables });
  };

  const handleVariableAdd = () => {
    updateVariables([...variables, { ...DEFAULT_VARIABLE }]);
  };

  const handleVariableDelete = (index: number) => {
    const filtered = variables.filter((_, i) => i !== index);
    updateVariables(filtered);
  };

  const confirmDelete = (event: React.MouseEvent, index: number) => {
    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: T("confirm-popup.are-you-really-want-to-delete-this-constant"),
      icon: "pi pi-exclamation-triangle",
      accept: () => handleVariableDelete(index),
    });
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as FreeTextDateVariable;
    const updated = variables.map((item, index) => (index === e.index ? newData : item));
    updateVariables(updated);
  };

  const nameEditorTemplate = (opt: ColumnEditorOptions) => (
    <InputText
      placeholder="Variable name"
      value={opt.rowData.name || ""}
      onChange={e => opt.editorCallback?.(e.target.value)}
    />
  );

  const truncateToEditorTemplate = (opt: ColumnEditorOptions) => (
    <Dropdown
      value={opt.rowData.truncateTo}
      options={timeUnitOptions}
      onChange={e => opt.editorCallback?.(e.value)}
      placeholder="Truncate to"
    />
  );

  const offsetEditorTemplate = (opt: ColumnEditorOptions) => (
    <InputNumber value={opt.rowData.offset ?? 0} onChange={e => opt.editorCallback?.(e.value)} />
  );

  const offsetUnitEditorTemplate = (opt: ColumnEditorOptions) => (
    <Dropdown
      value={opt.rowData.offsetUnit}
      options={timeUnitOptions}
      onChange={e => opt.editorCallback?.(e.value)}
      placeholder="Offset unit"
    />
  );

  const formatEditorTemplate = (opt: ColumnEditorOptions) => (
    <InputText
      placeholder="Format"
      value={opt.rowData.format || ""}
      onChange={e => opt.editorCallback?.(e.target.value)}
    />
  );

  const deleteButtonTemplate = (_: FreeTextDateVariable, options: ColumnBodyOptions) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger"
      onClick={e => confirmDelete(e, options.rowIndex)}
    />
  );

  const variablesHeader = (
    <div className="flex justify-content-between align-items-center">
      <span>{T("word.constants")}</span>
      <Button icon="pi pi-plus" className="p-button-success p-button-sm" onClick={handleVariableAdd} />
    </div>
  );

  const context: VariableReplacementContext = useMemo(
    () => ({
      dashboard,
      zoneId: tz.company,
      referenceTime: new Date().toISOString(),
      localDateVariables: config.variables,
    }),
    [dashboard, tz.company, config.variables]
  );

  const rawHtml = config.useHTMLCode ? (config.htmlContent ?? "") : (config.content ?? "");
  const previewHtml = useMemo(
    () => replaceVariables(rawHtml, context, tz.display, locale),
    [rawHtml, context, tz.display, locale]
  );

  return (
    <div className="flex flex-column gap-3">
      <div className="flex flex-column gap-2 w-2">
        <label htmlFor="height">Height (px)</label>
        <InputNumber
          id="height"
          value={config.height ?? undefined}
          onChange={e => onConfigChange({ ...config, height: e.value ?? undefined })}
        />
      </div>

      <DataTable
        value={variables}
        editMode="row"
        onRowEditComplete={handleRowEditComplete}
        header={variablesHeader}
        size="small"
        emptyMessage="-"
        scrollable
        scrollHeight="200px"
      >
        <Column field="name" header={T("word.name")} editor={nameEditorTemplate} />
        <Column field="truncateTo" header={T("word.truncate")} editor={truncateToEditorTemplate} />
        <Column field="offset" header={T("load-conf.offset")} editor={offsetEditorTemplate} />
        <Column field="offsetUnit" header={T("load-conf.offset-unit")} editor={offsetUnitEditorTemplate} />
        <Column
          field="format"
          header={T("component.date-format")}
          editor={formatEditorTemplate}
          className="w-11rem"
        />
        <Column rowEditor align="right" className="w-5rem p-0" />
        <Column body={deleteButtonTemplate} className="w-3rem" />
      </DataTable>

      <TabView onTabChange={e => handleTabChange(e.index)} activeIndex={activeIndex}>
        <TabPanel header="Text Editor">
          <ReactQuill
            value={config.content}
            onChange={e => onConfigChange({ ...config, content: e })}
            modules={reactQuillModules}
          />
        </TabPanel>
        <TabPanel header="HTML Editor">
          <Editor
            language="html"
            theme="vs-dark"
            value={config.htmlContent || ""}
            height="300px"
            onChange={value => onConfigChange({ ...config, htmlContent: value ?? "" })}
            options={{
              formatOnPaste: true,
              padding: {
                top: 10,
              },
            }}
          />
        </TabPanel>
      </TabView>

      <h4 className="border-bottom-1 pb-2 mb-2">Preview</h4>
      <div
        className="quill-rendered-content bg-white p-3"
        style={{ border: "1px solid #ddd", minHeight: "150px" }}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: previewHtml,
          }}
        />
      </div>
    </div>
  );
}
